import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  CircularProgress,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { apiFetch, buildApiUrl } from '../../../utils/apiClient';
import { logger } from '../../../utils/logger';
import EncounterEditFields from './EncounterEditFields';
import { useDialog } from '../../../contexts/DialogContext';
import { resolveMissedAppointmentFee } from '../../../utils/missedAppointmentFee';
import type { TimelineItem, BriefingSession } from '../hooks/useDashboardBriefing.types';

interface InlineAttendanceConfirmProps {
  session: TimelineItem | BriefingSession;
  onConfirmed?: () => void;
  onOpenAppointment?: (encounterId: string) => void;
  displayMode?: 'pill' | 'text';
  canEdit?: boolean;
}

/**
 * Inline attendance confirmation chip that expands to show editable
 * appointment type, duration, CPT code, and fee with auto-recalculation.
 * Uses shared EncounterEditFields to match BulkCompleteDialog exactly.
 */
export default function InlineAttendanceConfirm({
  session,
  onConfirmed,
  onOpenAppointment,
  displayMode = 'pill',
  canEdit = true,
}: InlineAttendanceConfirmProps) {
  const [expanded, setExpanded] = useState(false);
  const [attendanceStatus, setAttendanceStatus] = useState<string>(
    session.attendance_status || 'attended',
  );
  const [chargeFee, setChargeFee] = useState(false);
  const [feeAmount, setFeeAmount] = useState('');
  const [feeError, setFeeError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [confirmedStatus, setConfirmedStatus] = useState<string | null>(null);
  const { confirm } = useDialog();

  // Track editable field overrides from EncounterEditFields
  const editStateRef = useRef<{
    appointmentTypeId: string | null;
    durationMinutes: number | null;
    cptCodeId: string | null;
    fee: number | null;
    feeOverridden: boolean;
  }>({
    appointmentTypeId: session.appointment_type_id ?? null,
    durationMinutes: session.duration_minutes ?? null,
    cptCodeId: session.cpt_code_id ?? null,
    fee: null,
    feeOverridden: false,
  });

  const isAlreadyConfirmed = !!session.attendance_status;
  const isCancelled = session.status === 'cancelled';

  const pillSx = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 26,
    minWidth: 90,
    px: 1.5,
    fontSize: '0.75rem',
    fontWeight: 600,
    borderRadius: '13px',
    border: '1.5px solid',
    lineHeight: 1,
  } as const;
  const textSx = {
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: 26,
    px: 0,
    fontSize: '0.875rem',
    fontWeight: 600,
    lineHeight: 1.25,
  } as const;

  const isConfirmedState = isCancelled || isAlreadyConfirmed || confirmed;

  if (isCancelled && !isAlreadyConfirmed) {
    return null;
  }

  // Determine pill appearance
  let pillLabel = 'Confirm';
  let pillColors = {
    border: 'var(--kindredly-danger)',
    color: 'var(--kindredly-danger)',
    bg: 'color-mix(in srgb, var(--kindredly-danger) 8%, transparent)',
  };

  if (isConfirmedState) {
    const displayStatus = confirmed ? confirmedStatus : session.attendance_status;
    pillLabel =
      displayStatus === 'attended' ? 'Confirmed' :
      displayStatus === 'no_show' ? 'No Show' :
      displayStatus === 'late_cancel' ? 'Late Cancel' : 'Confirmed';
    const colorMap = {
      attended: {
        border: 'var(--kindredly-success)',
        color: 'var(--kindredly-success)',
        bg: 'color-mix(in srgb, var(--kindredly-success) 8%, transparent)',
      },
      no_show: {
        border: 'var(--kindredly-danger)',
        color: 'var(--kindredly-danger)',
        bg: 'color-mix(in srgb, var(--kindredly-danger) 8%, transparent)',
      },
      late_cancel: {
        border: 'var(--dst-warning-border)',
        color: 'var(--dst-warning-border)',
        bg: 'color-mix(in srgb, var(--dst-warning-border) 8%, transparent)',
      },
    };
    pillColors = colorMap[displayStatus as keyof typeof colorMap] ?? colorMap.attended;
  }
  if (!isConfirmedState) {
    pillLabel = 'Confirm attendance';
    pillColors = {
      border: 'var(--text-muted)',
      color: 'var(--text-muted)',
      bg: 'color-mix(in srgb, var(--text-muted) 8%, transparent)',
    };
  }

  const showFeeOptions = attendanceStatus === 'no_show' || attendanceStatus === 'late_cancel';

  // Backend read model: billing_fee_final is the canonical session fee (any
  // override already applied). Missing means unknown, never $0.
  const initialFee = session.billing_fee_final ?? null;

  const handleSubmit = async () => {
    if (!session.encounter_id) return;
    const resolved = await resolveMissedAppointmentFee(
      showFeeOptions && chargeFee,
      feeAmount,
      confirm,
    );
    if (resolved.error) setFeeError(resolved.error);
    if (resolved.abort) return;
    setSubmitting(true);
    try {
      const edits = editStateRef.current;
      const response = await apiFetch(
        buildApiUrl(`/api/calendar/appointments/${session.encounter_id}/attendance`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            attendance_status: attendanceStatus,
            charge_missed_fee: showFeeOptions && chargeFee,
            missed_appointment_fee: resolved.fee,
            appointment_type_id: edits.appointmentTypeId || undefined,
            duration_minutes: edits.durationMinutes || undefined,
            cpt_code_id: edits.cptCodeId || undefined,
            billing_fee_override: edits.feeOverridden ? edits.fee : undefined,
          }),
        },
      );
      if (response.ok) {
        setConfirmed(true);
        setConfirmedStatus(attendanceStatus);
        setExpanded(false);
        onConfirmed?.();
      }
    } catch (err) {
      logger.error('Error confirming attendance:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <Box
        component="button"
        type="button"
        onClick={() => setExpanded(!expanded)}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setExpanded(!expanded);
          }
        }}
        sx={{
          ...(displayMode === 'pill' ? pillSx : textSx),
          borderColor: pillColors.border,
          color: pillColors.color,
          bgcolor: displayMode === 'pill' ? pillColors.bg : 'transparent',
          border: displayMode === 'pill' ? '1.5px solid' : 'none',
          cursor: canEdit ? 'pointer' : 'default',
          '&:hover': { opacity: 0.85 },
        }}
        disabled={!canEdit}
        aria-expanded={expanded}
      >
        {pillLabel}
      </Box>

      <Collapse in={expanded}>
        <Box sx={{
          mt: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1,
          border: '1px solid', borderColor: 'divider',
        }}>
          {/* Attendance toggle */}
          <ToggleButtonGroup
            value={attendanceStatus}
            exclusive
            onChange={(_, val) => val && setAttendanceStatus(val)}
            size="small"
            sx={{ mb: 1.5 }}
          >
            <ToggleButton value="attended" sx={{
              px: 1.5, fontSize: '0.75rem',
              '&.Mui-selected': {
                bgcolor: 'var(--kindredly-success)',
                color: 'white',
                '&:hover': { bgcolor: 'var(--kindredly-success)' },
              },
            }}>Attended</ToggleButton>
            <ToggleButton value="no_show" sx={{
              px: 1.5, fontSize: '0.75rem',
              '&.Mui-selected': {
                bgcolor: 'var(--kindredly-danger)',
                color: 'white',
                '&:hover': { bgcolor: 'var(--kindredly-danger)' },
              },
            }}>No Show</ToggleButton>
            <ToggleButton value="late_cancel" sx={{
              px: 1.5, fontSize: '0.75rem',
              '&.Mui-selected': {
                bgcolor: 'var(--dst-warning-border)',
                color: 'white',
                '&:hover': { bgcolor: 'var(--dst-warning-border)' },
              },
            }}>Late Cancel</ToggleButton>
          </ToggleButtonGroup>

          {/* Shared editable encounter fields */}
          <EncounterEditFields
            appointmentTypeId={session.appointment_type_id}
            durationMinutes={session.duration_minutes}
            cptCodeId={session.cpt_code_id}
            initialFee={initialFee}
            patientId={session.patient_id}
            practitionerId={session.practitioner_id}
            caseId={(session as { case_id?: string | null }).case_id || undefined}
            adminFee={Number(session.admin_fee ?? 0)}
            adminFeeDescription={session.admin_fee_description}
            isPaymentOnFile={session.is_payment_on_file}
            paymentType={session.payment_type}
            splitPaymentEnabled={session.split_payment_enabled}
            onChange={(state) => { editStateRef.current = state; }}
            compact
            enabled={expanded}
          />

          {/* No-show / late cancel fee options */}
          {showFeeOptions && (
            <Box sx={{ mt: 1.5 }}>
              <FormControlLabel
                control={<Checkbox checked={chargeFee} onChange={(e) => setChargeFee(e.target.checked)} size="small" />}
                label={
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    Charge {attendanceStatus === 'no_show' ? 'no-show' : 'late cancellation'} fee
                  </Typography>
                }
              />
              {chargeFee && (
                <TextField
                  label="Fee Amount"
                  type="number"
                  size="small"
                  value={feeAmount}
                  error={!!feeError} helperText={feeError || undefined}
                  onChange={(e) => { setFeeError(''); setFeeAmount(e.target.value); }}
                  InputProps={{ startAdornment: <Box component="span" sx={{ mr: 0.5 }}>$</Box> }}
                  sx={{ mt: 0.5, width: 120 }}
                />
              )}
            </Box>
          )}

          {/* Action buttons */}
          <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button
              size="small"
              variant="contained"
              color={attendanceStatus === 'attended' ? 'success' : attendanceStatus === 'no_show' ? 'error' : 'warning'}
              onClick={handleSubmit}
              disabled={!canEdit || submitting}
              sx={{ fontSize: '0.75rem', textTransform: 'none' }}
            >
              {submitting ? <CircularProgress size={14} sx={{ mr: 0.5 }} /> : null}
              {attendanceStatus === 'attended' ? 'Confirm Attended' :
               attendanceStatus === 'no_show' ? 'Mark No Show' : 'Mark Late Cancel'}
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={() => setExpanded(false)}
              sx={{ fontSize: '0.75rem', textTransform: 'none' }}
            >
              Cancel
            </Button>
            {onOpenAppointment && session.encounter_id && (
              <Button
                size="small"
                variant="text"
                startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                onClick={() => onOpenAppointment(session.encounter_id)}
                sx={{ fontSize: '0.75rem', textTransform: 'none', ml: 'auto' }}
              >
                Edit Full Appointment
              </Button>
            )}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}
